import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMemberDietStats,
  fetchMemberTmeDietStats,
  fetchNutritionLogs14Days,
} from "../../../../redux/plans-reducers/plansSlice";
import PlanSummaryCard from "./PlanSummaryCard";
import StatsSummaryRow from "./StatsSummaryRow";
import CalorieIntakeAnalysis from "./CalorieIntakeAnalysis";
import NutritionLogsDays from "./NutritionLogsDays";
// import OversightCompliance from "./OversightCompliance";
import "../../../../styles/trainer-member/tme-nutrition-analysis.css";
import { convertStraight } from "../../../common-data/Converts";

const TMENutritionAnalysis = ({ userInfoData, rowData }) => {
  const { token } = userInfoData;
  const dispatch = useDispatch();
  const memberDietStats = useSelector((state) => state.plans?.memberDietStats);
  const memberTmeDietStats = useSelector(
    (state) => state.plans?.memberTmeDietStats
  );
  const nutritionLogs14Days = useSelector(
    (state) => state.plans?.nutritionLogs14Days
  );

  const apiData = memberDietStats?.data || {};

  const plan = {
    title: apiData?.diet_name || "Nutrition Plan",
    assignedBy: rowData?.trainer_name,
    duration: {
      start: convertStraight(memberDietStats?.plan_duration?.start_date),
      end: convertStraight(memberDietStats?.plan_duration?.end_date),
    },
    targets: {
      calories: Math.round(apiData?.total_cal || 0),
      protein: Math.round(apiData?.total_protien || 0),
      carbs: Math.round(apiData?.total_carb || 0),
      fats: Math.round(apiData?.total_fat || 0),
    },
    goals: [memberDietStats?.goal, apiData?.body_preference].filter(
      (item) => item && item.toString().trim() !== ""
    ),
    restrictions: [apiData?.diet_type].filter(
      (item) => item && item.toString().trim() !== ""
    ),
  };

  const tmeStatsData = memberTmeDietStats?.data?.[0];

  const summaryStats = rowData?.nutritionSummary || [
    {
      label: "Compliance",
      value: parseFloat(tmeStatsData?.compliance || 0),
      unit: "%",
      type: "progress",
    },
    {
      label: "Fully Logged",
      value: parseInt(tmeStatsData?.fully_consumed || 0),
      unit: "days",
    },
    {
      label: "Avg Calories",
      value: Math.round(parseFloat(tmeStatsData?.avg_cal_per_day || 0)),
      unit: "kcal/day",
    },
    {
      label: "Total Days",
      value: parseInt(tmeStatsData?.total_days || 0),
      unit: "days",
    },
  ];

  const transformApiDataToCalorieSeries = (apiData) => {
    if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
      return [];
    }

    return apiData.map((item) => {
      const formattedDate = convertStraight(item.date);

      return {
        date: formattedDate,
        calories: Math.round(parseFloat(item.total_cal) || 0),
      };
    });
  };

  const calorieSeries = transformApiDataToCalorieSeries(
    nutritionLogs14Days?.data || []
  );

  const avgCalories = Math.round(
    calorieSeries.reduce((s, d) => s + (d.calories || 0), 0) /
      (calorieSeries.length || 1)
  );
  const targetCalories = plan?.targets?.calories || 0;

  const transformApiDataToTimelineLogs = (apiData) => {
    if (!apiData || !Array.isArray(apiData) || apiData.length === 0) return [];

    return apiData.map((item, index) => {
      const formattedDate = convertStraight(item.date);

      const dietDetails = item.diet_details || {};
      const meals = Object.keys(dietDetails).map((mealKey) => {
        const displayName = mealKey
          .replace(/_/g, " ")
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        return {
          name: displayName,
          kcal: Math.round(dietDetails[mealKey] || 0),
        };
      });

      return {
        date: formattedDate,
        totalKcal: Math.round(parseFloat(item.total_cal) || 0),
        meals,
      };
    });
  };

  const transformApiDataToDetailedLogs = (apiData) => {
    if (!apiData || !Array.isArray(apiData) || apiData.length === 0) return [];

    return apiData.map((item, index) => {
      const formattedDate = convertStraight(item.date);

      const dietDetails = item.diet_details || {};
      const mealDetails = item.meal_details || [];
      const mealItemsMap = {};

      mealDetails.forEach((mealDetail) => {
        Object.keys(mealDetail).forEach((key) => {
          if (key === "meal_details" && Array.isArray(mealDetail[key])) {
            const mealType = Object.keys(mealDetail).find(
              (k) => k !== "meal_details"
            );
            if (mealType) {
              mealItemsMap[mealType] = mealDetail[key]
                .map((meal) => meal.meal_name)
                .filter((name) => name);
            }
          }
        });
      });

      const meals = Object.keys(dietDetails).map((mealKey) => {
        const displayName = mealKey
          .replace(/_/g, " ")
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        return {
          name: displayName,
          kcal: Math.round(dietDetails[mealKey] || 0),
          items: mealItemsMap[mealKey] || [],
        };
      });

      return {
        date: formattedDate,
        totalKcal: Math.round(parseFloat(item.total_cal) || 0),
        meals,
      };
    });
  };

  const apiNutritionData = nutritionLogs14Days?.data || [];
  const timelineLogs = transformApiDataToTimelineLogs(apiNutritionData);
  const detailedLogs = transformApiDataToDetailedLogs(apiNutritionData);

  useEffect(() => {
    const userId = rowData?.user_id;

    if (userId) {
      dispatch(fetchMemberDietStats({ filters: { user_id: userId }, token }));
      dispatch(
        fetchMemberTmeDietStats({ filters: { user_id: userId }, token })
      );
      dispatch(
        fetchNutritionLogs14Days({ filters: { user_id: userId }, token })
      );
    }
  }, []);

  return (
    <div className="tme-nutrition-analysis">
      <PlanSummaryCard plan={plan} />
      <StatsSummaryRow stats={summaryStats} />
      <CalorieIntakeAnalysis
        targetCalories={targetCalories}
        averageCalories={avgCalories}
        series={calorieSeries}
      />
      <NutritionLogsDays
        timelineLogs={timelineLogs}
        detailedLogs={detailedLogs}
      />
      {/* <OversightCompliance
        review={rowData?.trainerReview}
        compliance={rowData?.memberCompliance}
      /> */}
    </div>
  );
};

export default TMENutritionAnalysis;
